<template>
  <div>
    <TopNav />

    <main class="dashboard">
      <div class="dashboard-header">
        <h1 class="dashboard-title">
          <template v-if="user">Hallo, {{ firstName }}</template>
          <template v-else>Dashboard</template>
        </h1>
        <p class="dashboard-subtitle">Willkommen im R2-Reparaturtool.</p>
      </div>

      <section v-if="printerEmbedUrl" class="printer-card">
        <iframe
          class="printer-frame"
          title="Druckerauswahl"
          :src="printerEmbedUrl"
          :style="{ height: printerFrameHeight + 'px' }"
        ></iframe>
      </section>

      <div v-if="loading" class="spinner" aria-label="Lädt"></div>
      <div v-else-if="error" class="error">{{ error }}</div>

      <template v-else>
        <!-- Preisanfragen der Servicepartner. Stehen ganz oben: ein Partner wartet
             darauf und kann bis zur Entscheidung weder bestaetigen noch
             abschliessen. Bei einem Abruffehler bleibt der Bereich mit der
             Meldung stehen (dieselbe Regel wie bei den Ruecksendungen). -->
        <section v-if="priceRequests.length > 0 || priceRequestError" class="slips-card price-requests-card">
          <div class="slips-header">
            <h2 class="slips-title">Preisanfragen</h2>
            <span class="slips-subtitle">Servicepartner warten auf eine Entscheidung</span>
          </div>
          <div v-if="priceRequestError" class="error slips-error">{{ priceRequestError }}</div>
          <div v-if="priceRequestNotice" class="price-request-notice">{{ priceRequestNotice }}</div>
          <ul v-if="priceRequests.length > 0" class="slips-list">
            <li v-for="pr in priceRequests" :key="pr.stock_device_id" class="price-request-item">
              <div class="price-request-head">
                <router-link
                  :to="{ name: 'stockdevices', query: { q: pr.stock_number } }"
                  class="price-request-number"
                  title="Im Gerätelager öffnen"
                >{{ pr.stock_number }}</router-link>
                <span class="price-request-device">{{ pr.description }}</span>
                <span class="price-request-partner">{{ pr.servicepartner.description }}</span>
              </div>
              <div class="price-request-prices">
                <span class="price-request-pattern">{{ pr.error_pattern }}</span>
                <span class="price-request-arrow">
                  {{ pr.cost.reference_price === null ? 'kein Preis hinterlegt' : pr.cost.reference_price_label }}
                  →
                  <strong>{{ pr.cost.price_request?.amount_label }}</strong>
                </span>
                <span v-if="pr.cost.price_request?.timestamp" class="slips-subtitle">
                  angefragt {{ formatDateTime(pr.cost.price_request.timestamp) }}
                </span>
              </div>
              <p class="price-request-note">{{ pr.cost.price_request?.note }}</p>
              <div class="price-request-actions">
                <button
                  type="button"
                  class="btn btn-sm btn-primary"
                  :disabled="decidingId !== null"
                  @click="onAcceptPriceRequest(pr)"
                >{{ decidingId === pr.stock_device_id ? 'Bucht …' : 'Zusagen' }}</button>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-danger"
                  :disabled="decidingId !== null"
                  @click="onRejectPriceRequest(pr)"
                >Ablehnen …</button>
              </div>
            </li>
          </ul>
        </section>

        <section v-if="newMasters.length > 0" class="new-masters-card">
          <div class="new-masters-header">
            <h2 class="new-masters-title">Neu angelegte Master</h2>
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary"
              :disabled="acknowledgingAll"
              @click="onAcknowledgeAll"
            >
              {{ acknowledgingAll ? 'Markiere …' : 'Alle als gelesen markieren' }}
            </button>
          </div>
          <div v-if="hideError" class="error new-masters-error">{{ hideError }}</div>
          <ul class="new-masters-list">
            <li v-for="m in newMasters" :key="m.id" class="new-masters-item">
              <span class="new-masters-vendor">{{ m.vendor.description }}</span>
              <span class="new-masters-desc">{{ m.description }}</span>
              <div class="new-masters-actions">
                <button
                  type="button"
                  class="btn btn-sm btn-primary"
                  @click="goToMaster(m.id)"
                >Zum Artikel</button>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-secondary dash-hide-btn"
                  :disabled="hidingIds.has(m.id)"
                  @click="onHide(m)"
                >Ausblenden</button>
              </div>
            </li>
          </ul>
        </section>

        <section v-if="statTiles.length" class="dashboard-grid">
          <div v-for="tile in statTiles" :key="tile.label" class="tile">
            <div class="tile-label">{{ tile.label }}</div>
            <div class="tile-value" :style="tile.color ? { color: tile.color } : undefined">{{ tile.value }}</div>
          </div>
        </section>

        <!-- Rücksendungen, die unterwegs sind. Steht über den Lieferscheinen
             hinaus, weil ein Paket, das ankommt, dringender ist als eines, das
             noch gepackt wird. Vollständig angenommene verschwinden von selbst. -->
        <!-- Auch bei einem Fehler sichtbar: lag die Meldung im ausgeblendeten
             Bereich, war ein fehlgeschlagener Abruf von "nichts unterwegs"
             nicht zu unterscheiden - genau die Verwechslung ist am 07.09.2026
             passiert, als die Endpoints noch nicht ausgeliefert waren. -->
        <section v-if="returnSlips.length > 0 || returnError" class="slips-card">
          <div class="slips-header">
            <h2 class="slips-title">Rücksendungen</h2>
            <span class="slips-subtitle">Unterwegs von Servicepartnern</span>
          </div>
          <div v-if="returnError" class="error slips-error">{{ returnError }}</div>
          <ul v-if="returnSlips.length > 0" class="slips-list">
            <li v-for="slip in returnSlips" :key="slip.id" class="slips-item">
              <span class="slips-partner">
                {{ slip.slip_number }} · {{ slip.servicepartner.description }}
              </span>
              <span class="slips-count">
                {{ slip.received_count }} von {{ slip.total_count }} angenommen
              </span>
              <button
                type="button"
                class="btn btn-sm btn-primary"
                @click="onReceiveReturn(slip)"
              >Annehmen</button>
            </li>
          </ul>
        </section>

        <!-- Was zum Versand vorbereitet ist, je Servicepartner. Steht hier, weil es
             eine Arbeitsliste ist: was hier steht, will gepackt werden.

             Der Bereich bleibt auch leer stehen - er traegt den Weg zur
             Uebersicht aller Lieferscheine, und der darf nicht davon abhaengen,
             ob gerade etwas gepackt werden will. -->
        <section class="slips-card">
          <div class="slips-header">
            <h2 class="slips-title">Lieferscheine</h2>
            <span class="slips-subtitle">Zum Versand vorbereitete Geräte</span>
            <router-link :to="{ name: 'deliveryslips' }" class="slips-link">
              Alle Lieferscheine
            </router-link>
          </div>
          <div v-if="slipError" class="error slips-error">{{ slipError }}</div>
          <p v-if="slipRecipients.length === 0" class="slips-subtitle slips-none">
            Derzeit ist kein Gerät zum Versand vorbereitet.
          </p>
          <ul v-else class="slips-list">
            <li v-for="r in slipRecipients" :key="r.id" class="slips-item">
              <span class="slips-partner">{{ r.description }}</span>
              <span class="slips-count">
                {{ r.device_count }} {{ r.device_count === 1 ? 'Gerät' : 'Geräte' }}
              </span>
              <button
                type="button"
                class="btn btn-sm btn-primary"
                :disabled="creatingSlipFor !== null"
                @click="onCreateSlip(r)"
              >Lieferschein erstellen</button>
            </li>
          </ul>
        </section>
      </template>
    </main>

    <ConfirmModal
      ref="confirmModal"
      title="Artikel ausblenden"
      confirm-label="Ausblenden"
    />
    <AlertModal ref="alertModal" :title="alertTitle" />
    <DeliverySlipCreateModal ref="slipCreateModal" />
    <ReturnSlipReceiveModal ref="returnReceiveModal" />
    <ConfirmModal ref="acceptModal" title="Preisanfrage zusagen" confirm-label="Zusagen" />
    <PriceRequestRejectModal ref="rejectModal" />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue';
import { useRouter } from 'vue-router';
import TopNav from '../components/TopNav.vue';
import ConfirmModal from '../components/ConfirmModal.vue';
import AlertModal from '../components/AlertModal.vue';
import DeliverySlipCreateModal from '../components/DeliverySlipCreateModal.vue';
import ReturnSlipReceiveModal from '../components/ReturnSlipReceiveModal.vue';
import PriceRequestRejectModal from '../components/PriceRequestRejectModal.vue';
import { getCurrentUser, type CurrentUser } from '../services/auth';
import { getPeppiPrinterUrls } from '../services/me';
import { listNew, acknowledgeAllNewMasters, hide, type NewMaster } from '../services/masterarticles';
import { stats as fetchTicketStats, type TicketStats } from '../services/tickets';
import { listRecipients, type DeliverySlipRecipient } from '../services/deliverySlips';
import { listOpenReturnSlips, type ReturnSlip } from '../services/returnSlips';
import { decidePriceRequest, listOpenPriceRequests, type PriceRequest } from '../services/priceRequests';

const user = ref<CurrentUser | null>(null);
const loading = ref(true);
const error = ref<string | null>(null);
const newMasters = ref<NewMaster[]>([]);
const acknowledgingAll = ref(false);
const hideError = ref<string | null>(null);
const hidingIds = ref<Set<number>>(new Set());
const confirmModal = useTemplateRef<InstanceType<typeof ConfirmModal>>('confirmModal');
const alertModal      = useTemplateRef<InstanceType<typeof AlertModal>>('alertModal');
const slipCreateModal = useTemplateRef<InstanceType<typeof DeliverySlipCreateModal>>('slipCreateModal');
const returnReceiveModal = useTemplateRef<InstanceType<typeof ReturnSlipReceiveModal>>('returnReceiveModal');
const acceptModal        = useTemplateRef<InstanceType<typeof ConfirmModal>>('acceptModal');
const rejectModal        = useTemplateRef<InstanceType<typeof PriceRequestRejectModal>>('rejectModal');

// Offene Preisanfragen der Servicepartner (Geraete auf "Warte auf Preisfreigabe").
const priceRequests      = ref<PriceRequest[]>([]);
const priceRequestError  = ref<string | null>(null);
const priceRequestNotice = ref<string | null>(null);
const decidingId         = ref<number | null>(null);
const alertTitle      = ref('Lieferschein erstellen');

// Servicepartner mit vorbereiteten Geräten. Kommt fertig gezählt vom Server.
const slipRecipients = ref<DeliverySlipRecipient[]>([]);
const slipError      = ref<string | null>(null);
const returnSlips    = ref<ReturnSlip[]>([]);
const returnError    = ref<string | null>(null);
const creatingSlipFor = ref<number | null>(null);

const router = useRouter();
const firstName = computed(() => user.value?.vn?.trim() || user.value?.userName || '');

const ticketStats = ref<TicketStats | null>(null);

// Druckerauswahl kommt als iFrame aus dem Solo (eine Quelle für alle Tools). Die
// Höhe meldet die eingebettete Seite selbst — sonst würde ihr Dropdown abgeschnitten.
const printerEmbedUrl    = ref<string | null>(null);
const printerFrameHeight = ref<number>(60);

function onPrinterFrameMessage(event: MessageEvent): void {
  if (printerEmbedUrl.value === null) return;
  if (event.origin !== new URL(printerEmbedUrl.value).origin) return;
  const { type, height } = event.data ?? {};
  if (type === 'PEPPI_PRINTER_HEIGHT' && typeof height === 'number')
    printerFrameHeight.value = Math.max(40, Math.min(height, 500));
}

// Kacheln aus ECHTEN Daten: je Status eine Kachel (mit DB-Farbe) + „Heute
// angelegt" + „Gesamt". Datengetrieben, damit neue Status automatisch auftauchen.
const statTiles = computed(() => {
  const s = ticketStats.value;
  if (!s) return [];
  return [
    ...s.by_status.map(b => ({ label: b.status, value: b.count, color: b.status_fg || '' })),
    { label: 'Heute angelegt', value: s.created_today, color: '' },
    { label: 'Gesamt',         value: s.total,         color: '' },
  ];
});

onMounted(async () => {
  window.addEventListener('message', onPrinterFrameMessage);

  try {
    user.value = await getCurrentUser();
  } catch (e) {
    error.value = 'Profil konnte nicht geladen werden.';
    console.error(e);
  } finally {
    loading.value = false;
  }

  try {
    printerEmbedUrl.value = (await getPeppiPrinterUrls()).embedUrl;
  } catch (e) {
    console.error('Drucker-Kachel konnte nicht geladen werden:', e);
    // Card bleibt unsichtbar, übriges Dashboard funktional.
  }

  try {
    newMasters.value = await listNew();
  } catch (e) {
    console.error('Neue-Master-Liste konnte nicht geladen werden:', e);
    // Card bleibt unsichtbar, übriges Dashboard funktional.
  }

  try {
    ticketStats.value = await fetchTicketStats();
  } catch (e) {
    console.error('Kennzahlen konnten nicht geladen werden:', e);
    // Kacheln bleiben leer, übriges Dashboard funktional.
  }

  await loadSlipRecipients();
  await loadReturnSlips();
  await loadPriceRequests();
});

/** Offene Preisanfragen. Ein Fehler steht als Meldung im Bereich und kippt nichts. */
async function loadPriceRequests(): Promise<void> {
  priceRequestError.value = null;
  try {
    priceRequests.value = await listOpenPriceRequests();
  } catch (e) {
    console.error('Preisanfragen konnten nicht geladen werden:', e);
    priceRequestError.value = 'Die Preisanfragen konnten nicht geladen werden.';
  }
}

async function onAcceptPriceRequest(pr: PriceRequest): Promise<void> {
  const ok = await acceptModal.value?.open(
    `${pr.stock_number} (${pr.servicepartner.description}): ${pr.cost.price_request?.amount_label} `
    + `statt ${pr.cost.reference_price === null ? 'keinem hinterlegten Preis' : pr.cost.reference_price_label} zusagen? `
    + 'Der Betrag gilt nur für diesen Fall, die Rezeptur bleibt unverändert.',
  );
  if (ok !== true) return;
  await decide(pr, true, null);
}

async function onRejectPriceRequest(pr: PriceRequest): Promise<void> {
  const note = await rejectModal.value?.open(pr);
  // undefined = abgebrochen; null = abgelehnt ohne Begruendung.
  if (note === undefined) return;
  await decide(pr, false, note);
}

/**
 * Entscheidung buchen und den Bereich neu laden. Eine nicht versandte Mail ist
 * kein Fehler der Entscheidung — sie steht als Hinweis dabei.
 */
async function decide(pr: PriceRequest, accepted: boolean, note: string | null): Promise<void> {
  if (decidingId.value !== null) return;
  decidingId.value         = pr.stock_device_id;
  priceRequestError.value  = null;
  priceRequestNotice.value = null;
  try {
    const result = await decidePriceRequest(pr.stock_device_id, accepted, note);
    priceRequestNotice.value = result.mail_warning
      ?? `${pr.stock_number}: Anfrage ${accepted ? 'zugesagt' : 'abgelehnt'}, der Servicepartner wurde per Mail informiert.`;
  } catch (e) {
    console.error('Preisanfrage konnte nicht entschieden werden:', e);
    priceRequestError.value = e instanceof Error && e.message === 'nothing_to_decide'
      ? `${pr.stock_number}: Diese Anfrage wurde inzwischen schon entschieden.`
      : `${pr.stock_number}: Die Entscheidung konnte nicht gebucht werden.`;
  } finally {
    decidingId.value = null;
  }
  await loadPriceRequests();
}

function formatDateTime(value: string): string {
  const d = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat('de-AT', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(d);
}

async function loadSlipRecipients(): Promise<void> {
  slipError.value = null;
  try {
    slipRecipients.value = await listRecipients();
  } catch (e) {
    console.error('Vorbereitete Versände konnten nicht geladen werden:', e);
    slipError.value = 'Die vorbereiteten Versände konnten nicht geladen werden.';
  }
}

/**
 * Die Rücksendungen mit offenen Positionen. Ein Fehler hier darf das Dashboard
 * nicht kippen — er steht als Meldung im Bereich, wie bei den Versänden.
 */
async function loadReturnSlips(): Promise<void> {
  returnError.value = null;
  try {
    returnSlips.value = await listOpenReturnSlips();
  } catch (e) {
    console.error('Rücksendungen konnten nicht geladen werden:', e);
    returnError.value = 'Die Rücksendungen konnten nicht geladen werden.';
  }
}

/**
 * Rücksendung annehmen: das Modal verlangt das Abscannen und bucht geräteweise.
 * Danach neu laden — die Zählung stimmt sonst nicht, und eine vollständig
 * angenommene Rücksendung verschwindet aus der Liste.
 */
async function onReceiveReturn(slip: ReturnSlip): Promise<void> {
  const changed = await returnReceiveModal.value?.open(slip);
  if (changed === true) await loadReturnSlips();
}

/**
 * Lieferschein für einen Servicepartner: das Modal verlangt, dass jedes Gerät
 * abgescannt wird, und legt ihn dann an. Danach das Dashboard neu laden — die
 * Zählung stimmt sonst nicht mehr, und der Partner verschwindet ganz, wenn nichts
 * mehr offen ist.
 */
async function onCreateSlip(recipient: DeliverySlipRecipient): Promise<void> {
  if (creatingSlipFor.value !== null) return;
  creatingSlipFor.value = recipient.id;
  try {
    const created = await slipCreateModal.value?.open(recipient);
    // Auch nach einem Abbruch neu laden: im Modal kann ein Gerät entfernt worden sein.
    await reloadDashboard();
    if (!created) return;

    // Nicht gedruckt ist kein Fehler — der Lieferschein steht, das Papier liegt in
    // der Warteschlange. Beides in derselben Meldung, damit niemand darauf wartet.
    alertTitle.value = created.printed
      ? 'Lieferschein erstellt'
      : 'Lieferschein erstellt, aber nicht gedruckt';
    const head = `Lieferschein ${created.slip_number} mit ${created.device_count} Gerät(en) erstellt`;
    await alertModal.value?.open(created.printed
      ? `${head} und gedruckt.`
      : `${head}. ${created.print_warning ?? ''}`.trim());
  } finally {
    creatingSlipFor.value = null;
  }
}

/** Alles neu, was sich durch einen Lieferschein ändert. */
async function reloadDashboard(): Promise<void> {
  await Promise.all([
    loadSlipRecipients(),
    (async () => {
      try {
        ticketStats.value = await fetchTicketStats();
      } catch (e) {
        console.error('Kennzahlen konnten nicht geladen werden:', e);
      }
    })(),
  ]);
}

onBeforeUnmount(() => {
  window.removeEventListener('message', onPrinterFrameMessage);
});

function goToMaster(id: number): void {
  router.push({ name: 'masterarticle', params: { id } });
}

async function onHide(m: NewMaster): Promise<void> {
  if (hidingIds.value.has(m.id)) return;

  const confirmed = await confirmModal.value?.open(
    `„${m.description}" wirklich ausblenden? Der Artikel verschwindet aus den Listen und ist nicht mehr für Reparatur freigegeben.`,
  );
  if (!confirmed) return;

  hideError.value = null;
  hidingIds.value.add(m.id);
  try {
    await hide(m.id);
    newMasters.value = newMasters.value.filter(item => item.id !== m.id);
  } catch (e) {
    console.error('Ausblenden fehlgeschlagen:', e);
    hideError.value = 'Ausblenden fehlgeschlagen — bitte erneut versuchen.';
  } finally {
    hidingIds.value.delete(m.id);
  }
}

async function onAcknowledgeAll(): Promise<void> {
  if (newMasters.value.length === 0) return;
  const msg = `Wirklich alle ${newMasters.value.length} neu angelegten Master zur Kenntnis nehmen?`;
  if (!confirm(msg)) return;

  acknowledgingAll.value = true;
  try {
    await acknowledgeAllNewMasters();
    newMasters.value = [];
  } catch (e) {
    console.error('Bulk-Acknowledge fehlgeschlagen:', e);
    alert('Markieren fehlgeschlagen — bitte erneut versuchen.');
  } finally {
    acknowledgingAll.value = false;
  }
}
</script>

<style scoped>
.printer-card {
  margin-bottom: 24px;
  padding: 8px 16px;
  background: var(--surface, #fff);
  border-radius: var(--radius, 12px);
  box-shadow: var(--shadow-1), var(--shadow-2);
}

.printer-frame {
  display: block;
  width: 100%;
  border: 0;
}

.slips-card {
  /* Deutlich abgesetzt von den Kacheln darueber - anderer Zweck: dort Kennzahlen,
     hier eine Arbeitsliste. */
  margin-top: 40px;
  margin-bottom: 24px;
  padding: 20px 24px;
  background: var(--surface, #fff);
  border-radius: var(--radius, 12px);
  box-shadow: var(--shadow-1), var(--shadow-2);
}

.slips-header {
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 12px;
}

.slips-title {
  margin: 0;
  font-size: 17px;
  font-weight: 600;
}

.slips-subtitle {
  color: #6c757d;
  font-size: 14px;
}

/* Rechts im Kopf, damit der Weg zur Uebersicht immer an derselben Stelle liegt. */
.slips-link {
  margin-left: auto;
  font-size: 14px;
  white-space: nowrap;
}

.slips-none {
  margin: 0;
}

.slips-error {
  margin-bottom: 12px;
}

.slips-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.slips-item {
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 14px;
  align-items: center;
  padding: 8px 6px;
  border-radius: 6px;
}

.slips-item:hover {
  background: #f6f6f6;
}

.slips-count {
  color: #6c757d;
  font-size: 14px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .slips-item {
    grid-template-columns: 1fr auto;
    grid-template-areas:
      "partner count"
      "action  action";
    row-gap: 6px;
  }
}

.new-masters-card {
  margin-bottom: 24px;
  padding: 20px 24px;
  background: var(--surface, #fff);
  border-radius: var(--radius, 12px);
  box-shadow: var(--shadow-1), var(--shadow-2);
}

/* „Ausblenden" ist nicht destruktiv → neutraler Ghost, erst beim Hover rot. */
.dash-hide-btn:hover:not(:disabled) {
  color: var(--danger, #d70015);
  border-color: rgba(215, 0, 21, 0.35);
  background: rgba(215, 0, 21, 0.06);
}

.new-masters-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.new-masters-title {
  margin: 0;
  font-size: 17px;
  font-weight: 600;
}

.new-masters-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.new-masters-item {
  display: grid;
  grid-template-columns: minmax(140px, 200px) 1fr auto;
  gap: 14px;
  align-items: center;
  padding: 8px 6px;
  border-radius: 6px;
}

.new-masters-item:hover {
  background: #f6f6f6;
}

.new-masters-vendor {
  color: #6c757d;
  font-size: 14px;
}

.new-masters-actions {
  display: flex;
  gap: 8px;
}

.new-masters-error {
  margin-bottom: 12px;
}

.new-masters-desc {
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .new-masters-header {
    flex-direction: column;
    align-items: stretch;
  }

  .new-masters-item {
    grid-template-columns: 1fr auto;
    grid-template-areas:
      "vendor action"
      "desc   action";
    row-gap: 2px;
  }

  .new-masters-vendor { grid-area: vendor; }
  .new-masters-desc   { grid-area: desc; white-space: normal; }
  .new-masters-item > .new-masters-actions {
    grid-area: action;
    align-self: center;
    flex-direction: column;
  }
}

/* Preisanfragen: eine Zeile je Anfrage mit Kopf, Preisen, Begruendung, Knoepfen. */
.price-requests-card {
  border-left: 4px solid var(--accent, #f7a600);
}

.price-request-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 0;
  border-bottom: 1px solid #f1f3f5;
}

.price-request-item:last-child { border-bottom: none; }

.price-request-head {
  display: flex;
  gap: 12px;
  align-items: baseline;
  flex-wrap: wrap;
}

.price-request-number {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  text-decoration: none;
}

.price-request-device { font-weight: 500; }
.price-request-partner { color: #6c757d; }

.price-request-prices {
  display: flex;
  gap: 12px;
  align-items: baseline;
  flex-wrap: wrap;
}

.price-request-pattern { color: #6c757d; }
.price-request-arrow { font-variant-numeric: tabular-nums; }

.price-request-note {
  margin: 0;
  padding: 6px 10px;
  background: rgba(0, 0, 0, 0.03);
  border-radius: 6px;
  white-space: pre-wrap;
  font-size: 14px;
}

.price-request-actions {
  display: flex;
  gap: 8px;
  margin-top: 4px;
}

.price-request-notice {
  margin: 0 0 8px;
  padding: 6px 10px;
  border-radius: 6px;
  background: rgba(25, 135, 84, 0.10);
  color: #146c43;
  font-size: 14px;
}
</style>
